import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Toast } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import Select from 'react-select';
import moment from 'moment';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ConfirmModal from '../../components/common/ConfirmModal';
import NewsListItem from '../../components/News/List/Item';
import AsideLastNews from '../../components/News/List/AsideLastNews';
import useAPI from '../../hooks/useAPI';
import useUserContext from '../../hooks/useUserContext';
import { isAdmin, isMediaCenter_tst } from '../../services/rolesSvc';

import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import './NewsList.less';

export default function NewsList() {
  const api = useAPI();
  const history = useHistory();
  const userCtx = useUserContext();
  const placeholderRef = useRef(null)

  const [news, setNews] = useState([]);
  const [tagsAPI, setTagsAPI] = useState([]);
  const [isShowRemoveModal, setIsShowRemoveModal] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [tags, setTags] = useState(null);
  const [timePeriod, setTimePeriod] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState('');
  const [isDatepickerOpen, setDatepickerOpen] = useState(false);

  const getNewsListByPeriod = useCallback((newsList) => {
    return newsList.filter((newsEl) => {
      const start = timePeriod[0]?.toISOString()
      const end = timePeriod[1]?.toISOString()

      return start < newsEl.uploadDate && newsEl.uploadDate < end
    })
  }, [timePeriod])

  const getNewsListByTags = useCallback((newsList) => {
    if (!tags) return newsList;
    return newsList.filter((newsEl) => {
      const newsElemTagsIds = newsEl.tags.map((tag => tag.id));
      return newsElemTagsIds.includes(tags.value);
    });
  }, [tags]);

  const currentNewsList = useMemo(() => {
    if (tags) {
      const newsListByTags = getNewsListByTags(news);

      if (Boolean(timePeriod?.length)) {
        return getNewsListByPeriod(newsListByTags)
      }
      return newsListByTags

    } else if (Boolean(timePeriod?.length)) {
      return getNewsListByPeriod(news)
    }

    return news
  }, [getNewsListByPeriod, getNewsListByTags, tags, news])

  const showConfirmRemoveModal = (id) => {
    setIsShowRemoveModal(true);
    setSelectedPostId(id);
  };

  const hideConfirmRemoveModal = () => {
    setIsShowRemoveModal(false);
    setSelectedPostId(null);
  };

  const handlePostRemove = async () => {
    const respRemove = await api.del(`/news/${selectedPostId}`);
    if (respRemove && !respRemove.error) {
      setNews((prev) => prev.filter((n) => n.id !== respRemove.id));
      hideConfirmRemoveModal();
    }
  };

  const handleRoute = useCallback((id) => history.push(`/news/read/${id}`), []);

  const isUserAdminOrMediaCenter = useMemo(() => {
    return isAdmin(userCtx) || isMediaCenter_tst(userCtx);
  }, [isAdmin, isMediaCenter_tst, userCtx]);

  const handleFocusDatepicker = () => {
    placeholderRef?.current.classList.add('hide')
    setDatepickerOpen(true)
  };

  const handleCloseDatepicker = () => {
    setDatepickerOpen(false)
  };

  useEffect(async () => {
    const newsResp = await api.get('/news/tags');
    setTagsAPI(newsResp);
  }, []);

  useEffect(async () => {
    const newsResp = await api.get('/news');
    if (newsResp && !newsResp.error) {
      setNews(newsResp);
    }
  }, []);

  useEffect(() => {
    if (timePeriod && timePeriod[0]?.toISOString() > timePeriod[1]?.toISOString()) {
      setError('Дата начала периода не может быть позже даты окончания периода');
      setShowToast(true);
    }
  }, [timePeriod]);

  useEffect(() => {
    if (!isDatepickerOpen && !timePeriod) {
      placeholderRef?.current?.classList?.remove('hide')
    }
  }, [timePeriod, isDatepickerOpen]);

  return (
    <div className="news-page">
      <div className="news-page-toolbar">
        <div className="news-page-datepicker">
          <DateRangePicker
            className="react-daterange-picker"
            onChange={setTimePeriod}
            onCalendarClose={handleCloseDatepicker}
            onCalendarOpen={handleFocusDatepicker}
            onFocus={handleFocusDatepicker}
            isOpen={isDatepickerOpen}
            dayPlaceholder={'01'}
            monthPlaceholder={'01'}
            yearPlaceholder={'2023'} 
            format={'dd.MM.yyyy'}
            value={timePeriod}
          />
          <div className="datepicker-placeholder" ref={placeholderRef} onClick={handleFocusDatepicker}>Период</div>
        </div>

        {isUserAdminOrMediaCenter && (
          <Button onClick={() => history.push('/news/edit/new')}>
            Добавить новость
          </Button>
        )}
      </div>

      <Row>
        <Col xs={12} md={8}>
          <section className="news-page-list">
            {currentNewsList.length > 0 ? currentNewsList.map((newsEl) => (
              <div key={newsEl.id}>
                <NewsListItem
                  hash={newsEl.hash}
                  onHeaderClick={() => handleRoute(newsEl.id)}
                  uploadedOn={moment(newsEl.uploadDate).format('DD.MM.yyyy')}
                  header={newsEl.header}
                  post={newsEl.post}
                  tags={newsEl.tags}
                  imageId={newsEl.hasImage ? newsEl.imageFileId : null}
                  showActions={isUserAdminOrMediaCenter}
                  onEdit={() => history.push(`/news/edit/${newsEl.id}`)}
                  onRemove={() => showConfirmRemoveModal(newsEl.id)}
                  item={newsEl}
                />
              </div>
            )) : <p style={{ fontSize: '24px', flex: 'none', marginLeft: 'auto', marginRight: 'auto' }}>В этой рубрике нет новостей</p>}
          </section>
        </Col>
        <Col style={{ paddingLeft: '55px' }} xs={12} md={4}>
          {news[0] && (
            <AsideLastNews
              onHeaderClick={() => handleRoute(news[0].id, news[0].hash)}
              uploadedOn={moment(news[0].uploadDate).format('DD.MM.yyyy')}
              header={news[0].header}
              post={news[0].post}
              tags={news[0].tags}
            />
          )}
          <Button
            style={{ width: '100%', maxWidth: '400px', marginTop: '20px' }}
            onClick={() => {
              window.open('https://data.my1.urfu.ru/data/dashboards/projects?rating=%D0%98%D0%BD%D1%84%D0%BE%D0%BF%D0%BE%D0%B2%D0%BE%D0%B4%D1%8B', '_blank');
            }}
            variant="primary"
          >
            Рейтинг СТП/СЦ по количеству инфоповодов
          </Button>
          <div className="news-tags-list">
            {tagsAPI
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(tag => {
                const isSelected = tags && tags.value === tag.id;
                return (
                  <span
                    key={tag.id}
                    className="news-tags-item"
                    onClick={() => {
                      if (isSelected) {
                        setTags(null);
                      } else {
                        setTags({ value: tag.id, label: tag.name });
                      }
                    }}
                    style={{
                      textDecoration: isSelected ? 'underline' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {tag.name}
                  </span>
                );
              })}
          </div>
        </Col>
      </Row>

      <ConfirmModal
        show={isShowRemoveModal}
        onClose={hideConfirmRemoveModal}
        onConfirm={handlePostRemove}
        headerText="Удаление новости"
        message="Вы уверены, что хотите удалить данную новость?"
      />

      <Toast
        style={{
          position: 'fixed', top: 0, right: 0, color: 'white',
        }}
        className="d-inline-block m-3"
        bg="danger"
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={6000}
        autohide
      >
        <Toast.Body>
          {error}
        </Toast.Body>
      </Toast>
    </div>
  );
}
